import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Alert, Button, Spinner } from '@/components/ui';
import { useAuth } from '@/context/AuthContext';

export function TimezoneMigrationAlert() {
  const [needsMigration, setNeedsMigration] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, loading } = useAuth();

  useEffect(() => {
    const checkStatus = async () => {
      if (!user) return;
      setIsChecking(true);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('needs_timezone_migration')
          .eq('id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') { // Ignore row not found error
          throw error;
        }
        
        setNeedsMigration(data?.needs_timezone_migration ?? false);
      } catch (err) {
        console.error("Error checking migration flag:", err);
        setError("Could not check migration status.");
      } finally {
        setIsChecking(false);
      }
    };
    
    if (user && !loading) {
      checkStatus();
    }
  }, [user, loading]);

  const handleMigration = async () => {
    setIsMigrating(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('migrate_user_data_to_local_time');
      
      if (rpcError) throw rpcError;

      console.log('Migration result:', data);
      setNeedsMigration(false); // Hide alert on success
    } catch (err) {
      let detailedError = "An unknown error occurred. Please check the browser console for details.";
      if (err && typeof err === 'object' && 'message' in err) {
        detailedError = (err as { message: string }).message;
      }
      console.error("RPC migration error:", err);
      setError(`Migration failed: ${detailedError}`);
    } finally {
      setIsMigrating(false);
    }
  };

  if (isChecking || !needsMigration) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm">
      <Alert variant="default" className="p-4 rounded-lg shadow-lg">
        <h4 className="font-bold">Action Required: Update Timezone Data</h4>
        <p className="text-sm mt-2 mb-4">
          To ensure your progress is logged on the correct days, we need to update your existing data to your local timezone. This is a one-time update.
        </p>
        <Button onClick={handleMigration} disabled={isMigrating} className="w-full">
          {isMigrating ? (
            <><Spinner className="mr-2 h-4 w-4" /> Updating...</>
          ) : (
            'Update My Data'
          )}
        </Button>
        {error && (
          <div className="mt-3 p-2 bg-red-100/80 border border-red-300 rounded text-xs text-red-800">
            <p className="font-semibold">Error Details:</p>
            <p className="mt-1 font-mono">{error}</p>
          </div>
        )}
      </Alert>
    </div>
  );
} 